import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { optionLabel } from "@/lib/profileLabels";

/**
 * FilterSheet — Bottom-sheet (mobile) / side-drawer (desktop) filter UI.
 * Emits an object of active filters via `onApply`.
 * `initial` is the previously applied filter state.
 */
const DISTANCE_STEPS = [5, 10, 25, 50, 100, 250, 500];

function CheckGrid({ options, kind, selected, onChange, lang, testIdPrefix }) {
  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {options.map((opt) => {
        const isSel = selected.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => toggle(opt)}
            data-testid={`${testIdPrefix}-${opt}`}
            className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${
              isSel
                ? "bg-[hsl(var(--nvl-ink))] text-[hsl(var(--nvl-bg))] border-transparent"
                : "hover:bg-[hsl(var(--muted))]"
            }`}
            style={{ borderColor: isSel ? "transparent" : "hsl(var(--nvl-border))" }}
          >
            {optionLabel(lang, kind, opt)}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, children, id }) {
  return (
    <div className="py-5 border-b" style={{ borderColor: "hsl(var(--nvl-border))" }} data-testid={`filter-section-${id}`}>
      <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{title}</p>
      {children}
    </div>
  );
}

export default function FilterSheet({ open, onClose, initial, options, onApply }) {
  const { t, lang } = useI18n();
  const [state, setState] = useState(initial || {});
  const [enableCommunity, setEnableCommunity] = useState(!!(initial?.community_perspective?.length));

  useEffect(() => {
    if (open) {
      setState(initial || {});
      setEnableCommunity(!!(initial?.community_perspective?.length));
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const reset = () => {
    const cleared = { interests_match: "any" };
    setState(cleared);
    setEnableCommunity(false);
    onApply(cleared);
    onClose();
  };

  const apply = () => {
    const out = { ...state };
    if (!enableCommunity) delete out.community_perspective;
    // Clean empties
    Object.keys(out).forEach((k) => {
      const v = out[k];
      if (v === "" || v === null || v === undefined) delete out[k];
      if (Array.isArray(v) && v.length === 0) delete out[k];
    });
    onApply(out);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex md:items-stretch md:justify-end"
      style={{ backgroundColor: "rgba(10,17,40,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="filter-sheet-overlay"
    >
      <div
        className="ml-auto w-full md:max-w-md h-[90vh] md:h-full mt-auto md:mt-0 rounded-t-2xl md:rounded-none flex flex-col"
        style={{ backgroundColor: "hsl(var(--nvl-bg))", borderLeft: "1px solid hsl(var(--nvl-border))" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="filter-sheet"
      >
        <div
          className="flex items-center justify-between px-5 h-14 border-b sticky top-0 z-10"
          style={{ borderColor: "hsl(var(--nvl-border))", backgroundColor: "hsl(var(--nvl-bg))" }}
        >
          <h3 className="font-display text-xl">{t("app.discovery.filters.title")}</h3>
          <button onClick={onClose} data-testid="filter-close" aria-label="Close filters">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-32">
          <Section title={t("app.discovery.filters.age")} id="age">
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <label className="block text-xs mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                  {t("app.discovery.filters.ageMin")}
                </label>
                <input
                  type="number" min={18} max={99}
                  data-testid="filter-age-min"
                  value={state.age_min ?? 18}
                  onChange={(e) => set("age_min", Math.max(18, Math.min(99, parseInt(e.target.value || "18", 10))))}
                  className="w-full nvl-input"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                  {t("app.discovery.filters.ageMax")}
                </label>
                <input
                  type="number" min={18} max={99}
                  data-testid="filter-age-max"
                  value={state.age_max ?? 99}
                  onChange={(e) => set("age_max", Math.max(18, Math.min(99, parseInt(e.target.value || "99", 10))))}
                  className="w-full nvl-input"
                />
              </div>
            </div>
          </Section>

          <Section title={t("app.discovery.filters.distance")} id="distance">
            <div className="flex flex-wrap gap-2">
              {DISTANCE_STEPS.map((km) => {
                const isSel = state.distance_km === km;
                return (
                  <button
                    key={km}
                    type="button"
                    data-testid={`filter-distance-${km}`}
                    onClick={() => set("distance_km", isSel ? undefined : km)}
                    className={`text-xs px-3 py-2 rounded-md border ${isSel ? "bg-[hsl(var(--nvl-ink))] text-[hsl(var(--nvl-bg))] border-transparent" : "hover:bg-[hsl(var(--muted))]"}`}
                    style={{ borderColor: isSel ? "transparent" : "hsl(var(--nvl-border))" }}
                  >
                    {km} {t("app.discovery.km")}
                  </button>
                );
              })}
              <button
                type="button"
                data-testid="filter-distance-anywhere"
                onClick={() => set("distance_km", undefined)}
                className={`text-xs px-3 py-2 rounded-md border ${!state.distance_km ? "bg-[hsl(var(--nvl-ink))] text-[hsl(var(--nvl-bg))] border-transparent" : "hover:bg-[hsl(var(--muted))]"}`}
                style={{ borderColor: !state.distance_km ? "transparent" : "hsl(var(--nvl-border))" }}
              >
                {t("app.discovery.filters.anywhere")}
              </button>
            </div>
          </Section>

          <Section title={t("app.discovery.filters.countries")} id="countries">
            <input
              type="text"
              placeholder={t("app.discovery.filters.countriesPlaceholder")}
              data-testid="filter-countries"
              value={(state.countries || []).join(", ")}
              onChange={(e) => set("countries", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              className="w-full nvl-input"
            />
          </Section>

          <Section title={t("app.discovery.filters.cities")} id="cities">
            <input
              type="text"
              placeholder={t("app.discovery.filters.citiesPlaceholder")}
              data-testid="filter-cities"
              value={(state.cities || []).join(", ")}
              onChange={(e) => set("cities", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              className="w-full nvl-input"
            />
          </Section>

          <Section title={t("app.discovery.filters.interestedIn")} id="interested-in">
            <CheckGrid
              options={options?.interested_in || []}
              kind="interested_in" lang={lang}
              selected={state.interested_in || []}
              onChange={(v) => set("interested_in", v)}
              testIdPrefix="filter-gender"
            />
          </Section>

          <Section title={t("app.discovery.filters.relationshipGoals")} id="goals">
            <CheckGrid
              options={options?.relationship_goals || []}
              kind="relationship_goals" lang={lang}
              selected={state.relationship_goals || []}
              onChange={(v) => set("relationship_goals", v)}
              testIdPrefix="filter-goal"
            />
          </Section>

          <Section title={t("app.discovery.filters.verifiedOnly")} id="verified">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="filter-verified"
                checked={!!state.verified_only}
                onChange={(e) => set("verified_only", e.target.checked ? true : undefined)}
              />
              <span className="text-sm">{t("app.discovery.filters.verifiedOnly")}</span>
            </label>
          </Section>

          <Section title={t("app.discovery.filters.interests")} id="interests">
            <div className="flex gap-4 text-xs mb-3">
              {["any", "all"].map((mode) => (
                <label key={mode} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="interests-match"
                    checked={(state.interests_match || "any") === mode}
                    onChange={() => set("interests_match", mode)}
                    data-testid={`filter-interests-match-${mode}`}
                  />
                  {mode === "any" ? t("app.discovery.filters.interestsAny") : t("app.discovery.filters.interestsAll")}
                </label>
              ))}
            </div>
            <CheckGrid
              options={options?.interests || []}
              kind="interests" lang={lang}
              selected={state.interests || []}
              onChange={(v) => set("interests", v)}
              testIdPrefix="filter-interest"
            />
          </Section>

          <Section title={t("app.discovery.filters.values")} id="values">
            <CheckGrid
              options={options?.values || []}
              kind="values" lang={lang}
              selected={state.values || []}
              onChange={(v) => set("values", v)}
              testIdPrefix="filter-value"
            />
          </Section>

          <Section title={t("app.discovery.filters.lifestyle")} id="lifestyle">
            {[
              ["smoking", "smoking"],
              ["alcohol", "alcohol"],
              ["pets", "pets"],
              ["diet", "diet"],
            ].map(([key, kind]) => (
              <div key={key} className="mb-4">
                <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                  {t(`app.discovery.filters.${key}`)}
                </p>
                <CheckGrid
                  options={options?.[kind] || []}
                  kind={kind}
                  lang={lang}
                  selected={state[key] || []}
                  onChange={(v) => set(key, v)}
                  testIdPrefix={`filter-${key}`}
                />
              </div>
            ))}
          </Section>

          <Section title={t("app.discovery.filters.family")} id="family">
            {[
              ["children", "children"],
              ["wants_children", "wants_children"],
              ["open_to_children", "open_to_children"],
            ].map(([key, kind]) => (
              <div key={key} className="mb-4">
                <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                  {key === "children"
                    ? t("app.discovery.filters.children")
                    : key === "wants_children"
                    ? t("app.discovery.filters.wantsChildren")
                    : t("app.discovery.filters.openToChildren")}
                </p>
                <CheckGrid
                  options={options?.[kind] || []}
                  kind={kind}
                  lang={lang}
                  selected={state[key] || []}
                  onChange={(v) => set(key, v)}
                  testIdPrefix={`filter-${key}`}
                />
              </div>
            ))}
          </Section>

          <Section title={t("app.discovery.filters.religion")} id="religion">
            <CheckGrid
              options={options?.religion || []}
              kind="religion" lang={lang}
              selected={state.religion || []}
              onChange={(v) => set("religion", v)}
              testIdPrefix="filter-religion"
            />
          </Section>

          <Section title={t("app.discovery.filters.community")} id="community">
            <p className="text-xs mb-3 italic" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.discovery.filters.communityNote")}
            </p>
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                data-testid="filter-community-enable"
                checked={enableCommunity}
                onChange={(e) => {
                  setEnableCommunity(e.target.checked);
                  if (!e.target.checked) set("community_perspective", undefined);
                }}
              />
              <span className="text-sm">{t("app.discovery.filters.enableCommunity")}</span>
            </label>
            {enableCommunity ? (
              <CheckGrid
                options={options?.covid_perspective || []}
                kind="covid_perspective" lang={lang}
                selected={state.community_perspective || []}
                onChange={(v) => set("community_perspective", v)}
                testIdPrefix="filter-community"
              />
            ) : null}
          </Section>
        </div>

        <div
          className="p-4 border-t sticky bottom-0 flex gap-3"
          style={{ borderColor: "hsl(var(--nvl-border))", backgroundColor: "hsl(var(--nvl-bg))" }}
        >
          <button
            onClick={reset}
            className="nvl-btn nvl-btn-outline flex-1"
            data-testid="filter-reset"
          >
            {t("app.discovery.filters.reset")}
          </button>
          <button
            onClick={apply}
            className="nvl-btn nvl-btn-primary flex-1"
            data-testid="filter-apply"
          >
            {t("app.discovery.filters.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
